/**
 * Waypoint Resolver — resolves identifiers via WaypointDataProvider.
 * Platform-agnostic: no database dependencies.
 */
import type { ResolvedWaypoint, LatLng } from './types';
import type { WaypointDataProvider } from './waypoint-data-provider';
import { FlightPlanError } from './errors';

export class WaypointResolver {
  private provider: WaypointDataProvider;

  constructor(provider: WaypointDataProvider) {
    this.provider = provider;
  }

  async resolveWaypoint(identifier: string): Promise<ResolvedWaypoint | null> {
    const id = identifier.trim().toUpperCase();
    if (!id) return null;
    return this.provider.resolveIdentifier(id);
  }

  async resolveWaypointOrThrow(identifier: string): Promise<ResolvedWaypoint> {
    const result = await this.resolveWaypoint(identifier);
    if (!result) {
      throw new FlightPlanError(
        `Identifier '${identifier}' not found`,
        'INVALID_IDENTIFIER',
        { identifier },
      );
    }
    return result;
  }

  createUserWaypoint(position: LatLng, label?: string): ResolvedWaypoint {
    const id = label || `USR${Math.round(position.latitude * 100)}${Math.round(position.longitude * 100)}`;
    return {
      identifier: id,
      name: label || 'User Waypoint',
      type: 'user_defined',
      latitude: position.latitude,
      longitude: position.longitude,
      source: 'user',
    };
  }
}
